import React, { useEffect, useState } from "react";
import configuration from "../../Config/configuration";
import { config, errorNotify } from "../../utils/helper";
import { authorizedGet } from "../../Config/networkWithToken";
import Spinner from "../../Components/Spinner/Spinner";

const UserActivityLog = ({ userId }) => {
  const today = new Date().toISOString().split("T")[0];
  const [dates, setDates] = useState("today");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isApply, setIsApply] = useState(false);
  const [userName, setUserName] = useState("");
  const [activityData, setActivityData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const fetchData = async (filters = {}) => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (userId) params.append("id", userId);
      if (filters?.dateFilter) params.append("dateFilter", filters.dateFilter);
      if (filters?.startDate) params.append("startDate", filters.startDate);
      if (filters?.endDate) params.append("endDate", filters.endDate);

      const url = `${configuration.apis.userActivityLog}?${params.toString()}`;
      const res = await authorizedGet(url, config);

      const response = res?.data?.data;
      if (response) {
        setActivityData(response);
        setCurrentPage(1);
      }
      setUserName(response?.userName || "");
    } catch (err) {
      errorNotify("Failed to fetch activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({
      dateFilter: "custom",
      startDate: today,
      endDate: today,
    });
  }, [userId]);

  const requestSent = activityData?.requests?.sent || [];
  const requestAccepted = activityData?.requests?.accepted || [];
  const requestRejected = activityData?.requests?.rejected || [];
  const suggestionAccepted = activityData?.suggestion?.accepted || [];
  const suggestionRejected = activityData?.suggestion?.rejected || [];
  const questionnaireSent = activityData?.questionnaire?.sent || [];
  const questionnaireAccept = activityData?.questionnaire?.approved || [];
  const questionnaireReject = activityData?.questionnaire?.rejected || [];

  const maxLength = Math.max(
    requestSent.length,
    requestAccepted.length,
    requestRejected.length,
    suggestionAccepted.length,
    suggestionRejected.length,
    questionnaireSent.length,
    questionnaireAccept.length,
    questionnaireReject.length,
  );
  const totalPages = Math.ceil(maxLength / ITEMS_PER_PAGE);

  const paginate = (data) =>
    data.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE,
    );

  const paginatedRequestSent = paginate(requestSent);
  const paginatedRequestAccept = paginate(requestAccepted);
  const paginatedRequestReject = paginate(requestRejected);
  const paginatedSuggAccept = paginate(suggestionAccepted);
  const paginatedSuggReject = paginate(suggestionRejected);
  const paginatedQuestionnaireSent = paginate(questionnaireSent);
  const paginatedQuestionnaireApproved = paginate(questionnaireAccept);
  const paginatedQuestionnaireReject = paginate(questionnaireReject);

  const currentPageDataLength = Math.max(
    paginatedRequestSent.length,
    paginatedRequestAccept.length,
    paginatedRequestReject.length,
    paginatedSuggAccept.length,
    paginatedSuggReject.length,
    paginatedQuestionnaireSent.length,
    paginatedQuestionnaireApproved.length,
    paginatedQuestionnaireReject.length,
  );

  const handleApply = () => {
    const filters = {};
    setIsApply(true);

    const todayDate = new Date();
    const year = todayDate.getFullYear();

    if (dates === "custom") {
      if (!startDate.trim() || !endDate.trim()) {
        errorNotify("Please select both start and end dates.");
        return;
      }
      filters.dateFilter = "custom";
      filters.startDate = startDate;
      filters.endDate = endDate;
    } else if (dates === "today") {
      filters.dateFilter = "custom";
      filters.startDate = todayDate.toISOString().split("T")[0];
      filters.endDate = todayDate.toISOString().split("T")[0];
    } else if (dates === "thisWeek") {
      const firstDay = new Date(
        todayDate.setDate(todayDate.getDate() - todayDate.getDay()),
      );
      const lastDay = new Date(todayDate.setDate(firstDay.getDate() + 6));
      filters.dateFilter = "custom";
      filters.startDate = firstDay.toISOString().split("T")[0];
      filters.endDate = lastDay.toISOString().split("T")[0];
    } else if (dates === "thisMonth") {
      const start = new Date(year, todayDate.getMonth(), 1);
      const end = new Date(year, todayDate.getMonth() + 1, 0);
      filters.dateFilter = "custom";
      filters.startDate = start.toISOString().split("T")[0];
      filters.endDate = end.toISOString().split("T")[0];
    } else if (dates === "thisYear") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-01-01`;
      filters.endDate = `${year}-12-31`;
    } else if (dates === "q1") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-01-01`;
      filters.endDate = `${year}-03-31`;
    } else if (dates === "q2") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-04-01`;
      filters.endDate = `${year}-06-30`;
    } else if (dates === "q3") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-07-01`;
      filters.endDate = `${year}-09-30`;
    } else if (dates === "q4") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-10-01`;
      filters.endDate = `${year}-12-31`;
    } else if (dates === "h1") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-01-01`;
      filters.endDate = `${year}-06-30`;
    } else if (dates === "h2") {
      filters.dateFilter = "custom";
      filters.startDate = `${year}-07-01`;
      filters.endDate = `${year}-12-31`;
    } else {
      filters.dateFilter = dates; // fallback
    }

    fetchData(filters);
  };

  const handleClear = () => {
    setIsApply(false);
    setDates("today");
    setStartDate(today);
    setEndDate(today);
    setPage(1);
    fetchData({
      dateFilter: "custom",
      startDate: today,
      endDate: today,
    });
  };

  return (
    <div className="font-Poppins p-2">
      {loading ? (
        <div className="flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div>
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="mb-4 flex items-center justify-between pl-4">
                <h1 className="md:text-1xl pl-4 text-xl font-semibold">
                  {userName}'s Activity Log
                </h1>
              </div>

              <div className="mb-4 flex flex-wrap items-center gap-4">
                <select
                  value={dates}
                  onChange={(e) => setDates(e.target.value)}
                  className="cursor-pointer rounded border px-3 py-2 text-sm"
                >
                  <option value="today">Today</option>
                  <option value="thisWeek">This Week</option>
                  <option value="thisMonth">This Month</option>
                  <option value="thisYear">This Year</option>
                  <option value="q1">Q1 (Jan - Mar)</option>
                  <option value="q2">Q2 (Apr - Jun)</option>
                  <option value="q3">Q3 (Jul - Sep)</option>
                  <option value="q4">Q4 (Oct - Dec)</option>
                  <option value="h1">H1 (Jan - Jun)</option>
                  <option value="h2">H2 (Jul - Dec)</option>
                  <option value="custom">Custom Range</option>
                </select>

                {dates === "custom" && (
                  <>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="rounded border px-2 py-1 text-sm"
                    />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="rounded border px-2 py-1 text-sm"
                    />
                  </>
                )}

                <button
                  className="rounded bg-textYellow px-4 py-2 text-sm text-white"
                  disabled={dates === "custom" && (!startDate || !endDate)}
                  onClick={handleApply}
                >
                  Apply
                </button>

                {isApply && (
                  <button
                    className="rounded bg-red-700 px-4 py-2 text-sm text-white hover:bg-red-500"
                    onClick={handleClear}
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
          </div>

          {currentPageDataLength > 0 ? (
            <div className="pb-4 pl-4 pt-4">
              <div className="overflow-x-auto rounded-lg bg-white shadow-md">
                <table className="min-w-max divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className=" px-6 py-3 text-left text-sm font-medium uppercase">
                        Request Sent
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Request Accepted
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Request Declined
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Suggestion Accepted
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Suggestion Declined
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Questionnaire Sent
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Questionnaire Accept
                      </th>
                      <th className="whitespace-nowrap px-6 py-3 text-left text-sm font-medium uppercase">
                        Questionnaire Decline
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {Array.from({
                      length: Math.max(
                        paginatedRequestSent.length,
                        paginatedRequestAccept.length,
                        paginatedRequestReject.length,
                        paginatedSuggAccept.length,
                        paginatedSuggReject.length,
                        paginatedQuestionnaireSent.length,
                        paginatedQuestionnaireApproved.length,
                      ),
                    }).map((_, index) => (
                      <tr key={`row-${index}`}>
                        {/* Request Sent */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedRequestSent[index] && (
                            <>
                              <span>
                                Has sent request <br /> to{" "}
                                <strong>
                                  {paginatedRequestSent[index].user}
                                </strong>
                              </span>
                              <div>
                                <span className="font-medium">Sent at:</span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedRequestSent[index].sentAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                            </>
                          )}
                        </td>

                        {/* Request Accepted */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedRequestAccept[index] && (
                            <>
                              <span>
                                Has accepted the request <br /> from{" "}
                                <strong>
                                  {paginatedRequestAccept[index].requestedUser}
                                </strong>{" "}
                              </span>
                              <div>
                                <span className="font-medium">
                                  Accepted at:
                                </span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedRequestAccept[
                                      index
                                    ].userApprovedAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                            </>
                          )}
                        </td>
                        {/* Request Rejected */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedRequestReject[index] && (
                            <>
                              <span>
                                Has declined the request <br /> from{" "}
                                <strong>
                                  {paginatedRequestReject[index].requestedUser}
                                </strong>{" "}
                              </span>
                              <div>
                                <span className="font-medium">
                                  Declined at:
                                </span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedRequestReject[
                                      index
                                    ].userRejectedAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                              <div>
                                <span className="font-medium">
                                  Declined Reason:
                                </span>{" "}
                                <strong>
                                  {paginatedRequestReject[index].comments},
                                </strong>
                              </div>
                            </>
                          )}
                        </td>

                        {/* Suggestion Accepted */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedSuggAccept[index] && (
                            <>
                              <span>
                                Suggestion accepted from <br />
                                <strong>
                                  {paginatedSuggAccept[index].user === userName
                                    ? paginatedSuggAccept[index].matchedUserName
                                    : paginatedSuggAccept[index].user}
                                </strong>
                              </span>
                              <div>
                                <span className="font-medium">
                                  Accepted at:
                                </span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedSuggAccept[index].userDecisionAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                            </>
                          )}
                        </td>

                        {/* Suggestion Rejected */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedSuggReject[index] &&
                            (() => {
                              const item = paginatedSuggReject[index];
                              let rejectedBy = "";
                              let rejectedReason = "";
                              let rejectedAt = "";

                              const bothRejected =
                                item.Userdecision === "rejected" &&
                                item.matchedUserdecision === "rejected";

                              if (bothRejected) {
                                if (userId === item.userId) {
                                  // Logged in user is "user" → rejection came from matchedUser
                                  rejectedBy = item.matchedUserName;
                                  rejectedReason = item.matchedUserComments;
                                  rejectedAt = item.matchedUserDecisionAt;
                                } else if (userId === item.matchedUser) {
                                  // Logged in user is "matchedUser" → rejection came from user
                                  rejectedBy = item.user;
                                  rejectedReason = item.userComments;
                                  rejectedAt = item.userDecisionAt;
                                }
                              } else if (item.Userdecision === "rejected") {
                                // rejected by user
                                rejectedBy = item.user;
                                rejectedReason = item.userComments;
                                rejectedAt = item.userDecisionAt;
                              } else if (
                                item.matchedUserdecision === "rejected"
                              ) {
                                // rejected by matchedUser
                                rejectedBy = item.matchedUserName;
                                rejectedReason = item.matchedUserComments;
                                rejectedAt = item.matchedUserDecisionAt;
                              }

                              return (
                                <>
                                  <span>
                                    Suggestion declined By <br />
                                    <strong>{rejectedBy}</strong>
                                  </span>
                                  <div>
                                    <span className="font-medium">
                                      Declined at:
                                    </span>{" "}
                                    <strong>
                                      {rejectedAt
                                        ? new Date(
                                            rejectedAt,
                                          ).toLocaleDateString()
                                        : "-"}
                                    </strong>
                                  </div>
                                  <div>
                                    <span className="font-medium">
                                      Declined Reason:
                                    </span>{" "}
                                    <strong>{rejectedReason || "-"}</strong>
                                  </div>
                                </>
                              );
                            })()}
                        </td>

                        {/* Questionnaire Sent */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedQuestionnaireSent[index] && (
                            <>
                              <span>
                                Questionnaire sent <br /> to{" "}
                                <strong>
                                  {paginatedQuestionnaireSent[index].user}
                                </strong>
                              </span>
                              <div>
                                <span className="font-medium">Sent at:</span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedQuestionnaireSent[index].sentAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                            </>
                          )}
                        </td>
                        {/* Questionnaire Accept */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedQuestionnaireApproved[index] && (
                            <>
                              <span>
                                Questionnaire accepted for <br /> the user{" "}
                                <strong>
                                  {paginatedQuestionnaireApproved[index]
                                    .user === userName
                                    ? paginatedQuestionnaireApproved[index]
                                        .requestedUser
                                    : paginatedQuestionnaireApproved[index]
                                        .user}
                                </strong>
                              </span>
                              <div>
                                <span className="font-medium">
                                  Accepted at:
                                </span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedQuestionnaireApproved[
                                      index
                                    ].userApprovalAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                            </>
                          )}
                        </td>
                        {/* Questionnaire Accept */}
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {paginatedQuestionnaireReject[index] && (
                            <>
                              <span>
                                Have Declined the Questionnaire <br /> request
                                for{" "}
                                <strong>
                                  {paginatedQuestionnaireReject[index].user ===
                                  userName
                                    ? paginatedQuestionnaireReject[index]
                                        .requestedUser
                                    : paginatedQuestionnaireReject[index].user}
                                </strong>
                              </span>
                              <div>
                                <span className="font-medium">
                                  Declined at:
                                </span>{" "}
                                <strong>
                                  {new Date(
                                    paginatedQuestionnaireReject[
                                      index
                                    ].userApprovalAt,
                                  ).toLocaleDateString()}
                                </strong>
                              </div>
                              <div>
                                <span className="font-medium">
                                  Declined Reason:
                                </span>{" "}
                                <strong>
                                  {paginatedQuestionnaireReject[index].comments}
                                </strong>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="py-6 text-center text-black">
              No activity found for the selected filters.
            </div>
          )}
          <div className="mt-6 flex items-center justify-center space-x-2">
            <button
              className="rounded bg-gray-200 px-3 py-1 hover:bg-gray-300 disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="rounded bg-gray-200 px-3 py-1 hover:bg-gray-300 disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivityLog;
